import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { Flag, Loader2, ShieldAlert } from "lucide-react";

import { getErrorMessage } from "@/lib/errorMessage";
// Shown after login when an admin has reset the user's password — they must set their own
// new password before continuing. No current password is required (they just authenticated).
export default function ForcePasswordChange() {
  const { setUser, logout } = useAuth();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error("Passwords do not match."); return; }
    if (pw.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { new_password: pw });
      const me = await api.get("/auth/me");
      setUser(me.data);
      toast.success("Password set. Welcome in.");
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not set password"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background grain">
      <div className="w-full max-w-sm" data-testid="force-password-change">
        <div className="flex items-center gap-2 mb-8">
          <div className="h-9 w-9 grid place-items-center bg-[#D5001C] rounded-sm"><Flag className="h-5 w-5 text-white" /></div>
          <span className="font-head font-bold tracking-tight text-lg">PEC ATL OPS</span>
        </div>
        <div className="flex items-center gap-2 mb-1 text-amber-400">
          <ShieldAlert className="h-5 w-5" />
          <h2 className="font-head text-2xl font-bold tracking-tight text-foreground">Set a new password</h2>
        </div>
        <p className="text-sm text-zinc-500 mb-6">Your password was reset by an administrator. Choose a new one to continue.</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="label-tag" htmlFor="fnp">New password</Label>
            <PasswordInput id="fnp" testId="force-new-password" value={pw} onChange={(e) => setPw(e.target.value)}
              required className="mt-1.5 bg-secondary border-zinc-800 rounded-sm" />
          </div>
          <div>
            <Label className="label-tag" htmlFor="fnp2">Confirm new password</Label>
            <PasswordInput id="fnp2" testId="force-confirm-password" value={pw2} onChange={(e) => setPw2(e.target.value)}
              required className="mt-1.5 bg-secondary border-zinc-800 rounded-sm" />
          </div>
          <Button type="submit" data-testid="force-submit-btn" disabled={busy}
            className="w-full bg-[#D5001C] hover:bg-[#B90018] rounded-sm font-semibold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save and continue"}
          </Button>
          <button type="button" onClick={logout} className="w-full text-sm text-zinc-500 hover:text-zinc-300">Sign out</button>
        </form>
      </div>
    </div>
  );
}
